import React, {useState, useEffect} from 'react';
import {useParams, Link, useNavigate} from 'react-router-dom';
import {useAuth} from '../security/AuthContext';
import {Card, Badge, Modal, Button, Form, Alert} from 'react-bootstrap';
import useFastPhotos from '../fastPhotos/useFastPhotos';
import {apiClient} from '../api/ApiClient';
import './ProfileViewComponent.css';
import SlidingEmailForm from './SlidingEmailForm';
import {Ban, Flag} from 'lucide-react';
import {FaHeartCirclePlus} from "react-icons/fa6";
import {MdOutlineEmail} from "react-icons/md";
import {LuEye} from "react-icons/lu";
import ProfileModal from '../profileModelPopup/ProfileModal';

import {
  FaThumbsUp, FaUserCircle, FaSmile, FaMapMarkerAlt, FaHeart,
  FaPlane, FaCalendarAlt, FaBriefcase, FaNewspaper
} from 'react-icons/fa';


const ProfileViewComponent = ({isOwnProfile = false}) => {
  const navigate = useNavigate();
  const {passMemberTypeId, passMemberIdViewed} = useParams();
  
  // Profile states
  const [profile, setProfile] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  // Action states
  const [winkSuccess, setWinkSuccess] = useState(false);
  const [favoriteSuccess, setFavoriteSuccess] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState(false);
  const [hasWinked, setHasWinked] = useState(false);
  const [hasFavorited, setHasFavorited] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailText, setEmailText] = useState('');
  const [isBlocked, setIsBlocked] = useState(false);
  const [isReported, setIsReported] = useState(false);

  const [blockSuccess, setBlockSuccess] = useState(false);
  const [reportSuccess, setReportSuccess] = useState(false);

  const {fastPhotos, loading, error} = useFastPhotos();

  const [selectedProfile, setSelectedProfile] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);

  // Function to record profile view
  const recordProfileView = async (memberId) => {
    try {
      console.log('Recording view for profile ID:', memberId);
      
      const response = await apiClient.post('/rest/recordView', {
        viewedMemberId: memberId
      });
      
      console.log('View recorded response:', response.data);
    } catch (error) {
      console.error('Error recording profile view:', error);
    }
  };

  // Handle click on a close match profile
  const handleCloseMatchClick = async (e, photo) => {
    e.preventDefault();
    const token = localStorage.getItem('token');

    if (token) {
      // Record the profile view
      recordProfileView(photo[0]);
      // Navigate to the profile page
      navigate(`/profile/view/1/${photo[0]}`);
    } else {
      navigate('/login');
    }
  };

  // Action handlers
  const sendMemberAction = async (action, memberId) => {
    try {
      const response = await apiClient.post(`/rest/membercontact/${action}`, {
        updatedColumns: {
          profile: [{
            name: action === 'wink' ? 'P_MEMBER_ID_WINKED_AT' : 'P_MEMBER_ID_FAVORITED',
            val: memberId.toString()
          }]
        }
      });
      return response.data;
    } catch (error) {
      console.error(`Error sending ${action}:`, error);
      return null;
    }
  };

  const handleWinkClick = async (e) => {
    e.preventDefault();
    if (hasWinked) return;

    const result = await sendMemberAction('wink', profile[0]);
    if (result && !result.error) {
      setWinkSuccess(true);
      setHasWinked(true);
      setTimeout(() => {
        setWinkSuccess(false);
      }, 3000);
    }
  };

  const handleFavoriteClick = async (e) => {
    e.preventDefault();
    if (hasFavorited) return;

    const result = await sendMemberAction('favorite', profile[0]);
    if (result && !result.error) {
      setFavoriteSuccess(true);
      setHasFavorited(true);
      setTimeout(() => {
        setFavoriteSuccess(false);
      }, 3000);
    }
  };

  const handleBlockMember = async () => {
    if (isBlocked) return;

    try {
      const response = await apiClient.post(`/rest/blockMember`, {
        blockedMemberId: profile[0]
      });

      if (response.status === 200) {
        setIsBlocked(true);  // Keep button disabled permanently
        setBlockSuccess(true);  // Show success message
        setTimeout(() => {
          setBlockSuccess(false);  // Hide success message after 3 seconds
        }, 3000);
      }
    } catch (error) {
      console.error('Error blocking member:', error);
    }
  };

  const handleReportMember = async () => {
    if (isReported) return;

    try {
      const response = await apiClient.post(`/rest/reportMember`, {
        reportedMemberId: profile[0]
      });

      if (response.status === 200) {
        setIsReported(true);  // Keep button disabled permanently
        setReportSuccess(true);  // Show success message
        setTimeout(() => {
          setReportSuccess(false);  // Hide success message after 3 seconds
        }, 3000);
      }
    } catch (error) {
      console.error('Error reporting member:', error);
    }
  };


  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    try {
      // Update the request body to match the stored procedure parameters
      const response = await apiClient.post('/rest/sendmail', {
        emailToMemberId: Number(profile[0]),
        to: "dummy@dummy.com", // Required by DTO but not used in stored proc
        subject: "New Message", // Required by DTO but not used in stored proc
        body: emailText
      });


      if (response.data) {
        // Check the response status from the stored procedure
        if (response.data.emailstatus === 'BLOCKED_YOU') {
          alert('This member has blocked you from sending emails.');
        } else if (response.data.emailstatus === 'YOU_BLOCKED') {
          alert('You have blocked this member.');
        } else if (response.data.emailstatus === 'UPGRADED_BLOCK_FREE') {
          alert('This member requires an upgraded account to email them.');
        } else if (response.data.emailstatus === 'EMAILED') {
          setEmailSuccess(true);
          setShowEmailForm(false);
          setEmailText('');
          setTimeout(() => {
            setEmailSuccess(false);
          }, 3000);
        }
      }
    } catch (error) {
      console.error('Error sending email:', error);
    }
  };


  // Modal handlers
  const openModal = (index) => {
    setCurrentPhotoIndex(index);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const nextPhoto = () => {
    setCurrentPhotoIndex((prevIndex) => (prevIndex + 1) % photos.length);
  };

  const prevPhoto = () => {
    setCurrentPhotoIndex((prevIndex) => (prevIndex - 1 + photos.length) % photos.length);
  };

  // Load profile data
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    setIsLoading(true);
    setPhotos([]);
    setProfile(null);
    setCurrentPhotoIndex(0);

    // Reset ALL states when profile changes
    setHasWinked(false);
    setHasFavorited(false);
    setWinkSuccess(false);
    setFavoriteSuccess(false);
    setEmailSuccess(false);
    setShowEmailForm(false);
    setIsBlocked(false);   // Add this
    setIsReported(false);  // Add this
    setBlockSuccess(false);  // Add this
    setReportSuccess(false); // Add this
    setEmailText('');       // Add this for completeness

    
    const endpoint = isOwnProfile
      ? '/rest/profile/view/my-profile'
      : `/rest/profile/view/${Number(passMemberTypeId)}/${Number(passMemberIdViewed)}`;

    apiClient.get(endpoint)
      .then(response => {
        const data = response.data;
        setProfile(data[0]);
        setPhotos(data.slice(1));
        setIsLoading(false);
      })
      .catch(error => {
        console.error('Profile fetch error:', error);
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          navigate('/login');
        }
        setFetchError(error);
        setIsLoading(false);
      });
  }, [isOwnProfile, passMemberTypeId, passMemberIdViewed, navigate]);

  // Loading and error states
  if (isLoading) return <div>Loading profile...</div>;
  if (fetchError) return <div>Error loading profile. Please try again later.</div>;
  if (!profile) return <div>No profile data available</div>;


  return (
    <div className="profile-view-container">
      <div className="profile-main">
        {profile && photos.length > 0 && (
          <div className="profile-header" onClick={() => openModal(0)}>
            <h1>{profile[5]}</h1>
            <img className="profile-img" src={`https://pics.netfixup.com/${photos[0][1]}/${photos[0][0]}`} alt="Profile" />

            {photos.length > 0 && (
              <div className="profile-section">
                <ul className="photo-list">
                  {photos.map((photo, index) => (
                    <li key={index} onClick={() => openModal(index)}>
                      <img src={`https://pics.netfixup.com/${photo[1]}/${photo[0]}`} alt="Profile" />
                    </li>
                  ))}
                </ul>
              </div>
            )}

          </div>
        )}



        {!isOwnProfile && (
          <>
            <div className="action-buttons-container">

              <div className="action-button">
                {blockSuccess && <div className="success-message">✓ Blocked</div>}
                <Button
                  className={`full-profile-link ${isBlocked ? 'disabled-button' : ''}`}
                  size="sm"
                  onClick={handleBlockMember}
                  disabled={isBlocked}
                >
                  <Ban size={16} color="black" /> {isBlocked ? 'Blocked' : 'Block'}
                </Button>
              </div>

              <div className="action-button">
                {reportSuccess && <div className="success-message">✓ Reported</div>}
                <Button
                  className={`full-profile-link ${isReported ? 'disabled-button' : ''}`}
                  size="sm"
                  onClick={handleReportMember}
                  disabled={isReported}
                >
                  <Flag size={16} color="black" /> {isReported ? 'Reported' : 'Report'}
                </Button>
              </div>




              <div className="action-button">
                {emailSuccess && <div className="success-message">✓ Email Sent</div>}
                <Button className="full-profile-link" size="sm" onClick={() => setShowEmailForm(true)}>
                  <MdOutlineEmail color="black" size={16} /> Email
                </Button>
              </div>

              <div className="action-button">
                {favoriteSuccess && <div className="success-message">✓ Favorited</div>}
                <Button
                  className={`full-profile-link ${hasFavorited ? 'disabled-button' : ''}`}
                  size="sm"
                  onClick={handleFavoriteClick}
                  disabled={hasFavorited}
                >
                  <LuEye color="black" size={16} /> {hasFavorited ? 'Favorited' : 'Favorite'}
                </Button>
              </div>

              <div className="action-button">
                {winkSuccess && <div className="success-message">✓ Winked</div>}
                <Button
                  className={`full-profile-link ${hasWinked ? 'disabled-button' : ''}`}
                  size="sm"
                  onClick={handleWinkClick}
                  disabled={hasWinked}
                >
                  <LuEye color="black" size={16} /> {hasWinked ? 'Winked' : 'Wink'}
                </Button>
              </div>
            </div>

            <SlidingEmailForm
              show={showEmailForm}
              onClose={() => setShowEmailForm(false)}

              onSubmit={async (text) => {
                try {
                  const response = await apiClient.post('/rest/sendmail', {
                    emailToMemberId: Number(profile[0]),
                    to: "dummy@dummy.com",
                    subject: "New Message",
                    body: text
                  });

                  if (response.data) {
                    setEmailSuccess(true);
                    setShowEmailForm(false);
                    setTimeout(() => {
                      setEmailSuccess(false);
                    }, 3000);
                  }
                } catch (error) {
                  console.error('Error sending email:', error);
                }
              }}
            />


            {emailSuccess && (
              <div className="mb-4">
                <Alert variant="success">
                  ✓ Email sent successfully!
                </Alert>
              </div>
            )}
          </>
        )}

        <div className="details-view-grid">
          <div className="detail-view-item"><div className="detail-view-label">About: {profile[2]} {profile[4] === 'M' ? 'Male' : 'Female'}</div></div>

          <div className="detail-view-item"><div className="detail-view-label">Username: {profile[1] || 'Not Selected'}</div></div>
          <div className="detail-view-item"><div className="detail-view-label">Seeking {profile[7] === 'M' ? 'Male' : 'Female'} {profile[8]}-{profile[9]}</div></div>
          <div className="detail-view-item"><div className="detail-view-label">Online: {profile[33] || 'Not Selected'}</div></div>

          <div className="detail-view-item"><div className="detail-view-label">Location: {profile[26]}, {profile[27]}</div></div>
          <div className="detail-view-item"><div className="detail-view-label">Horoscope: {profile[48] || 'Not Selected'}</div></div>
          <div className="detail-view-item"><div className="detail-view-label">Family Plan: {profile[47] || 'Not Selected'}</div></div>
          <div className="detail-view-item"><div className="detail-view-label">Job Desc: {profile[50] || 'Not Selected'}</div></div>
          <div className="detail-view-item"><div className="detail-view-label">Living:  {profile[37] || 'Not Selected'}</div></div>
          <div className="detail-view-item"><div className="detail-view-label">Ethnicity: {profile[51] || 'Not Selected'}</div></div>
          <div className="detail-view-item"><div className="detail-view-label">Relationship: {profile[37] || 'Not Selected'}</div></div>

          <div className="detail-view-item"><div className="detail-view-label">Body Type: {profile[34] || 'Not Selected'}</div></div>
          <div className="detail-view-item"><div className="detail-view-label">Eye Color: {profile[35] || 'Not Selected'}</div></div>
          <div className="detail-view-item"><div className="detail-view-label">Best Body: {profile[49] || 'Not Selected'}</div></div>

          <div className="detail-view-item"><div className="detail-view-label">Edu: {profile[39] || 'Not Selected'}</div></div>
          <div className="detail-view-item"><div className="detail-view-label">Religon: {profile[40] || 'Not Selected'}</div></div>
          <div className="detail-view-item"><div className="detail-view-label">Political: {profile[41] || 'Not Selected'}</div></div>
          <div className="detail-view-item"><div className="detail-view-label">Living Status: {profile[42] || 'Not Selected'}</div></div>
          <div className="detail-view-item"><div className="detail-view-label">Income: {profile[43] || 'Not Selected'}</div></div>
          <div className="detail-view-item"><div className="detail-view-label">Drink Alchol: {profile[44] || 'Not Selected'}</div></div>
          <div className="detail-view-item"><div className="detail-view-label">Smoke: {profile[45] || 'Not Selected'}</div></div>
          <div className="detail-view-item"><div className="detail-view-label">Employ: {profile[46] || 'Not Selected'}</div></div>
          <div className="detail-view-item"><div className="detail-view-label">Type: {profile[23] || 'Not Selected'}</div></div>


        </div>

        {/* Wrap all sections in a grid container */}
        <div className="profile-sections-grid">
          <div className="profile-section">
            <h2>Introduction</h2>
            <p>{profile?.[14] || ''}</p>
          </div>

          <div className="profile-section">
            <h2>Headline</h2>
            <p>{profile?.[21] || ''}</p>
          </div>

          <div className="profile-section">
            <h2>Hangouts</h2>
            <p>{profile?.[16] || ''}</p>
          </div>

          <div className="profile-section">
            <h2>Hobbies</h2>
            <p>{profile?.[17] || ''}</p>
          </div>

          <div className="profile-section">
            <h2>Destinations</h2>
            <p>{profile?.[18] || ''}</p>
          </div>

          <div className="profile-section">
            <h2>First Date</h2>
            <p>{profile?.[19] || ''}</p>
          </div>

          <div className="profile-section">
            <h2>Desc Job</h2>
            <p>{profile?.[20] || ''}</p>
          </div>

          <div className="profile-section">
            <h2>Fun</h2>
            <p>{profile?.[15] || ''}</p>
          </div>
        </div>

      </div>


      <div className="results-right-sidebar">
        <h5>Close Matches</h5>
        {loading ? (
          <p>Loading...</p>
        ) : error ? (
          <p>{error}</p>
        ) : fastPhotos && fastPhotos.length > 0 ? (
          fastPhotos.map((photo, index) => (
            photo && photo[0] && photo[7] && photo[6] ? (
              <Card 
                key={index} 
                className="fast-photo-card-side"
                style={{ cursor: 'pointer' }}
              >
                {/* Changed from Link to anchor with onClick handler */}
                <a 
                  href={`/profile/view/1/${photo[0]}`}
                  onClick={(e) => handleCloseMatchClick(e, photo)}
                >
                  <Card.Img variant="top" src={`https://pics.netfixup.com/${photo[7]}${photo[6]}`} />
                  <Badge pill bg="light" text="dark">
                    {photo[2]}
                  </Badge>
                </a>
              </Card>
            ) : null
          ))
        ) : (
          <p>No Fast Photos Found</p>
        )}
      </div>


      <Modal show={showModal} onHide={closeModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Photo Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {photos && photos.length > 0 && currentPhotoIndex < photos.length && (
            <img
              src={`https://pics.netfixup.com/${photos[currentPhotoIndex][1]}/${photos[currentPhotoIndex][0]}`}
              alt="Profile"
              style={{width: '100%', maxHeight: '400px', objectFit: 'contain'}}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={prevPhoto} disabled={!photos || photos.length <= 1}>
            Previous
          </Button>
          <Button variant="primary" onClick={nextPhoto} disabled={!photos || photos.length <= 1}>
            Next
          </Button>
        </Modal.Footer>
      </Modal>



    </div>
  );
};

export default ProfileViewComponent;